import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppHeader } from "../components/AppHeader";
import { Watermark } from "../components/Watermark";
import { apiFetch } from "../lib/api";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import type {
  MediaRef,
  Post,
  PostsListResponse,
} from "@family/shared";

interface FlatMedia {
  media: MediaRef;
  post: {
    id: string;
    title: string | null;
    eventDate: string | null;
    createdAt: string;
    placeName?: string | null;
    authorName?: string | null;
  };
  /** Names of tagged persons on this media item */
  taggedNames: string[];
}

type GroupBy = "year" | "person" | "place";

function flatten(posts: Post[]): FlatMedia[] {
  const out: FlatMedia[] = [];
  for (const p of posts) {
    for (const m of p.media) {
      out.push({
        media: m,
        post: {
          id: p.id,
          title: p.title,
          eventDate: p.eventDate,
          createdAt: p.createdAt,
          placeName: p.placeName,
          authorName: p.author.displayName,
        },
        taggedNames: m.taggedPersons.map((t) =>
          [t.givenName, t.familyName].filter(Boolean).join(" "),
        ),
      });
    }
  }
  return out;
}

function yearOf(p: FlatMedia): number {
  return new Date(p.post.eventDate ?? p.post.createdAt).getUTCFullYear();
}

function groupItems(
  items: FlatMedia[],
  groupBy: GroupBy,
): Map<string, FlatMedia[]> {
  const map = new Map<string, FlatMedia[]>();

  for (const fm of items) {
    let keys: string[];
    switch (groupBy) {
      case "year":
        keys = [String(yearOf(fm))];
        break;
      case "person":
        keys = fm.taggedNames.length > 0 ? fm.taggedNames : ["Untagged"];
        break;
      case "place":
        keys = fm.post.placeName ? [fm.post.placeName] : ["No location"];
        break;
    }
    for (const key of keys) {
      const arr = map.get(key) ?? [];
      arr.push(fm);
      map.set(key, arr);
    }
  }

  return map;
}

export function GalleryPage() {
  const meQuery = useMe();
  const primary = resolveMembership(meQuery.data);
  const slug = primary?.familySlug;

  const [groupBy, setGroupBy] = useState<GroupBy>("year");
  const [filterYear, setFilterYear] = useState<string>("");
  const [filterPerson, setFilterPerson] = useState<string>("");
  const [filterType, setFilterType] = useState<"all" | "photo" | "video">("all");

  const query = useQuery<PostsListResponse>({
    queryKey: ["posts", slug],
    queryFn: () => apiFetch<PostsListResponse>(`/families/${slug}/posts`),
    enabled: Boolean(slug),
  });

  const all = useMemo(() => flatten(query.data?.posts ?? []), [query.data]);

  // Derive available years and people for filter dropdowns
  const availableYears = useMemo(() => {
    const set = new Set<number>();
    for (const fm of all) set.add(yearOf(fm));
    return [...set].sort((a, b) => b - a);
  }, [all]);

  const availablePeople = useMemo(() => {
    const set = new Set<string>();
    for (const fm of all) {
      for (const name of fm.taggedNames) set.add(name);
    }
    return [...set].sort();
  }, [all]);

  // Apply filters
  const filtered = useMemo(() => {
    let items = all;
    if (filterYear) {
      const y = Number(filterYear);
      items = items.filter((fm) => yearOf(fm) === y);
    }
    if (filterPerson) {
      items = items.filter((fm) => fm.taggedNames.includes(filterPerson));
    }
    if (filterType === "photo") {
      items = items.filter((fm) => fm.media.kind === "photo");
    } else if (filterType === "video") {
      items = items.filter((fm) => fm.media.kind === "video");
    }
    return items;
  }, [all, filterYear, filterPerson, filterType]);

  // Group
  const grouped = useMemo(() => groupItems(filtered, groupBy), [filtered, groupBy]);
  const sortedKeys = useMemo(() => {
    const keys = [...grouped.keys()];
    if (groupBy === "year") {
      return keys.sort((a, b) => Number(b) - Number(a));
    }
    // Alphabetical, but push "Untagged"/"No location" to end
    return keys.sort((a, b) => {
      if (a === "Untagged" || a === "No location") return 1;
      if (b === "Untagged" || b === "No location") return -1;
      return a.localeCompare(b);
    });
  }, [grouped, groupBy]);

  if (!primary || !slug) {
    return (
      <>
        <AppHeader />
        <main className="page">
          <p className="text-muted">Loading…</p>
        </main>
      </>
    );
  }

  const watermarkVars = {
    user:
      meQuery.data?.user.displayName ??
      meQuery.data?.user.email ??
      meQuery.data?.user.phone ??
      "",
    family: primary.familyName,
    phone: meQuery.data?.user.phone ?? null,
    email: meQuery.data?.user.email ?? null,
  };

  return (
    <>
      <AppHeader />
      <main className="page stack-lg">
        <h1>Photos &amp; Videos</h1>

        {/* Controls */}
        <div style={{ display: "flex", gap: "var(--s-2)", flexWrap: "wrap", alignItems: "end" }}>
          <div>
            <label htmlFor="gallery-group" style={{ fontSize: "0.75rem", display: "block", marginBottom: 2 }}>Group by</label>
            <select
              id="gallery-group"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as GroupBy)}
              style={{ fontSize: "0.85rem", padding: "6px 8px", minHeight: 32 }}
            >
              <option value="year">Year</option>
              <option value="person">Person</option>
              <option value="place">Location</option>
            </select>
          </div>
          <div>
            <label htmlFor="gallery-year" style={{ fontSize: "0.75rem", display: "block", marginBottom: 2 }}>Year</label>
            <select
              id="gallery-year"
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              style={{ fontSize: "0.85rem", padding: "6px 8px", minHeight: 32 }}
            >
              <option value="">All years</option>
              {availableYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="gallery-person" style={{ fontSize: "0.75rem", display: "block", marginBottom: 2 }}>Person</label>
            <select
              id="gallery-person"
              value={filterPerson}
              onChange={(e) => setFilterPerson(e.target.value)}
              style={{ fontSize: "0.85rem", padding: "6px 8px", minHeight: 32 }}
            >
              <option value="">All people</option>
              {availablePeople.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="gallery-type" style={{ fontSize: "0.75rem", display: "block", marginBottom: 2 }}>Type</label>
            <select
              id="gallery-type"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as "all" | "photo" | "video")}
              style={{ fontSize: "0.85rem", padding: "6px 8px", minHeight: 32 }}
            >
              <option value="all">All</option>
              <option value="photo">Photos</option>
              <option value="video">Videos</option>
            </select>
          </div>
        </div>

        {/* Stats */}
        <p className="text-muted" style={{ fontSize: "0.85rem", margin: 0 }}>
          {filtered.length} item{filtered.length !== 1 ? "s" : ""}
          {filtered.length !== all.length && ` (of ${all.length} total)`}
        </p>

        {query.isLoading && <p className="text-muted">Loading…</p>}
        {query.error && (
          <p className="text-error">
            Error: {(query.error as Error).message}
          </p>
        )}
        {!query.isLoading && filtered.length === 0 && (
          <p className="text-muted">
            {all.length === 0
              ? "No photos yet. Upload some by creating a post."
              : "No items match your filters."}
          </p>
        )}

        {sortedKeys.map((key) => (
          <section key={key} className="stack">
            <h2 style={{ fontSize: "1.3rem" }}>{key}</h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                gap: "var(--s-2)",
              }}
            >
              {grouped.get(key)!.map((fm) => (
                <Link
                  key={fm.media.id}
                  to={`/post/${fm.post.id}`}
                  title={fm.post.title ?? "Open post"}
                  style={{
                    display: "block",
                    position: "relative",
                    aspectRatio: "1 / 1",
                    overflow: "hidden",
                    borderRadius: 4,
                    background: "var(--c-bg-alt)",
                  }}
                >
                  {fm.media.kind === "video" ? (
                    <>
                      {fm.media.thumbnailUrl ? (
                        <img
                          src={fm.media.thumbnailUrl}
                          alt="Video thumbnail"
                          loading="lazy"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                            pointerEvents: "none",
                          }}
                          draggable={false}
                          onContextMenu={(e) => e.preventDefault()}
                        />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            background: "var(--c-bg-alt)",
                          }}
                        />
                      )}
                      {/* Play button overlay */}
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          pointerEvents: "none",
                        }}
                      >
                        <div
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: "50%",
                            background: "rgba(0,0,0,0.6)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                            <polygon points="8,5 20,12 8,19" />
                          </svg>
                        </div>
                      </div>
                    </>
                  ) : (
                    <img
                      // Grid cells are ~150px square; the original was the
                      // worst offender here, a whole library of multi-megabyte
                      // photos loaded to be drawn as thumbnails. Videos on the
                      // branch above already did this.
                      src={fm.media.thumbnailUrl ?? fm.media.viewUrl}
                      alt=""
                      loading="lazy"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        pointerEvents: "none",
                      }}
                      draggable={false}
                      onContextMenu={(e) => e.preventDefault()}
                    />
                  )}
                  <Watermark
                    config={primary.watermark}
                    variables={watermarkVars}
                  />
                </Link>
              ))}
            </div>
          </section>
        ))}
      </main>
    </>
  );
}
